import { Autocomplete, Box, Button, Checkbox, Divider, FormControlLabel, Paper, TextField } from "@mui/material";
import Grid from "@mui/material/Grid2";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import DataTable from "../../components/common/DataTable";
import DatePicker from "../../components/common/DatePicker";
import Subheading from "../../components/common/Subheading";
import Title from "../../components/common/Title";
import { ValidationAlert } from "../../components/common/ValidationAlert";
import Layout from "../../components/layout/Layout";
import TopBarGridContainer from "../../components/layout/TopBar";
import useAlert from "../../hooks/useAlert";
import { useCancellableCeleryPoll } from "../../hooks/useCancellableCeleryPoll";
import { PLATFORM_NAMES } from "../Auto4Client/constants/platformNames";
import { CHECK_LABELS } from "../PreflightChecks/constants/preflightConstants";
import { getCeleryTaskLog } from "../../services/celery";
import {
  cancelPipelineCompany,
  cancelPipelineRun,
  getPipelineRuns,
  initClassificationPipeline,
} from "../../services/classificationPipeline";
import { getMasterCompanyNames } from "../../services/gsuite/sheets";
import { runPreflightChecksForBatch } from "../../services/preflightChecks";
import { applyCeleryPollOutcomeToAlert } from "../../utils/celeryPollAlert";
import CancelCompanyDialog from "./components/CancelCompanyDialog";
import CancelRunDialog from "./components/CancelRunDialog";
import {
  getPipelineRunColumns,
  getRunConfig,
  mapPipelineHistoryRuns,
  PIPELINE_HISTORY_COLUMNS,
  POLL_ALERT_COPY,
} from "./constants/pipelineConstants";
import { usePipelineRunPolling } from "./hooks/usePipelineRunPolling";

/**
 * Start a classification pipeline batch, or reopen a previous run from the
 * history table / `?run=` query param.
 *
 * @returns {JSX.Element}
 */
function ClassificationPipeline() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const runIdFromUrl = searchParams.get("run");
  const { alertState, showAlert, closeAlert } = useAlert();
  const pollTask = useCancellableCeleryPoll();
  // Latest selected task_id — in-flight fetches ignore stale responses.
  const selectedRunIdRef = useRef(null);
  // Last `?run=` id the restore effect tried, so a failed load isn't retried in a loop.
  const restoredRunIdRef = useRef(null);

  const [selectedCompanies, setSelectedCompanies] = useState([]);
  // Companies that run full (client) mode; every other selected company runs competitor mode.
  const [clientCompanies, setClientCompanies] = useState([]);
  const [companyNames, setCompanyNames] = useState([]);
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [includePreviouslyClassified, setIncludePreviouslyClassified] = useState(true);
  const [skipGeocoder, setSkipGeocoder] = useState(false);
  const [startDate, setStartDate] = useState(dayjs().subtract(1, "day"));
  const [endDate, setEndDate] = useState(dayjs());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [batchRunId, setBatchRunId] = useState(null);
  const [expectedCount, setExpectedCount] = useState(0);
  const [pipelineReport, setPipelineReport] = useState(null);
  const [previousRuns, setPreviousRuns] = useState([]);
  // Celery batch status: pending | completed | failed | cancelled. Poll only while pending.
  const [selectedBatchStatus, setSelectedBatchStatus] = useState(null);
  // Whether the open run skipped geocoding — worth seeing when reopening a run whose data looks off.
  const [selectedSkipGeocoder, setSelectedSkipGeocoder] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  // A drain was requested for the open run: running tasks finish, nothing new starts.
  const [selectedDraining, setSelectedDraining] = useState(false);
  // Company whose cancel button was clicked, while the confirm dialog is open.
  const [companyToCancel, setCompanyToCancel] = useState(null);
  // Companies the pre-run checks flagged, held while the user decides what to do about them.
  const [preflightFailures, setPreflightFailures] = useState([]);
  const [preflightPassing, setPreflightPassing] = useState([]);

  /** Companies finished — poll celery-task-logs until the LLM report is stored. */
  const handleReadyForReport = useCallback(async () => {
    const taskId = selectedRunIdRef.current;
    if (!taskId) {
      return;
    }
    showAlert("info", "Generating report...");
    try {
      const { outcome, result } = await pollTask(taskId);
      if (selectedRunIdRef.current !== taskId) {
        return; // User opened a different run while this poll was in flight.
      }
      setPipelineReport(result?.report ?? null);
      if (outcome === "completed") {
        setSelectedBatchStatus("completed");
      } else if (outcome === "failed") {
        setSelectedBatchStatus("failed");
      } else if (outcome === "cancelled") {
        setSelectedBatchStatus("cancelled");
      }
      applyCeleryPollOutcomeToAlert(showAlert, outcome, null, POLL_ALERT_COPY);
    } catch {
      if (selectedRunIdRef.current !== taskId) {
        return;
      }
      showAlert("error", POLL_ALERT_COPY.fallback.message);
    } finally {
      if (selectedRunIdRef.current === taskId) {
        setIsSubmitting(false);
      }
    }
  }, [pollTask, showAlert]);

  /** Poll live progress only for an in-progress batch; completed runs fetch once. */
  // Cancel buttons only while the batch is running. Memoised so the grid keeps its column state.
  const pipelineRunColumns = useMemo(
    () => getPipelineRunColumns(selectedBatchStatus === "pending" ? setCompanyToCancel : null),
    [selectedBatchStatus],
  );

  const { pipelineRuns, geocoderLogs, refresh: refreshPipelineRuns } = usePipelineRunPolling(
    batchRunId,
    expectedCount,
    {
      enabled: selectedBatchStatus === "pending" && !!batchRunId,
      onReadyForReport: handleReadyForReport,
    },
  );

  /**
   * Refill the form with the inputs a previous batch was started with.
   *
   * Fields missing from `config` keep their current form values.
   *
   * @param {object} config - The batch's `run_config` (see getRunConfig)
   */
  const applyRunConfig = useCallback((config) => {
    const companies = config.companies ?? [];
    const competitorModeCompanies = config.competitor_mode_companies ?? [];
    setSelectedCompanies(companies);
    setClientCompanies(companies.filter((name) => !competitorModeCompanies.includes(name)));
    if (config.platforms) {
      setSelectedPlatforms(config.platforms);
    }
    if (config.start_date && config.end_date) {
      setStartDate(dayjs(config.start_date));
      setEndDate(dayjs(config.end_date));
    }
    if (config.include_previously_classified !== undefined) {
      setIncludePreviouslyClassified(Boolean(config.include_previously_classified));
    }
    setSkipGeocoder(Boolean(config.skip_geocoder));
    // Warnings from an earlier check don't apply to the reloaded companies.
    setPreflightFailures([]);
    setPreflightPassing([]);
  }, []);

  /**
   * Rehydrate a batch from its celery log: company count, status, and report.
   * Live (pending) runs then poll progress; completed runs load once.
   *
   * @param {string} taskId - Celery task_id / batch_run_id
   * @param {{ syncUrl?: boolean, force?: boolean }} [options] - Write `?run=` unless
   *   restoring from the URL; `force` re-reads the already-selected run
   */
  const selectRun = useCallback(
    async (taskId, { syncUrl = true, force = false } = {}) => {
      if (!taskId || (!force && taskId === selectedRunIdRef.current)) {
        return;
      }
      const previousId = selectedRunIdRef.current;
      selectedRunIdRef.current = taskId;
      setPipelineReport(null);
      if (syncUrl) {
        setSearchParams({ run: taskId }, { replace: true });
      }

      const response = await getCeleryTaskLog(taskId);
      if (selectedRunIdRef.current !== taskId) {
        return;
      }
      if (!(response instanceof Response) || !response.ok) {
        selectedRunIdRef.current = previousId;
        showAlert("error", "Could not load pipeline run.");
        return;
      }
      const body = await response.json();
      const log = body.celery_task_log;
      if (!log) {
        selectedRunIdRef.current = previousId;
        showAlert("error", "Could not load pipeline run.");
        return;
      }
      const runConfig = getRunConfig(log);
      const missingConfigNote = "This run's platforms and dates weren't recorded.";
      if (runConfig) {
        applyRunConfig(runConfig);
      } else {
        // Older runs that never finished only kept their companies and skip_geocoder.
        applyRunConfig({ companies: log.companies ?? [], skip_geocoder: log.skip_geocoder });
      }

      setExpectedCount((log.companies ?? []).length);
      setSelectedBatchStatus(log.status ?? "pending");
      setSelectedSkipGeocoder(Boolean(log.skip_geocoder));
      setSelectedDraining(Boolean(log.drain_requested));
      if (log.status === "completed") {
        setPipelineReport(log.result?.report ?? null);
      } else if (log.status === "failed") {
        const headline = POLL_ALERT_COPY.failed.headline;
        showAlert("error", runConfig ? headline : `${headline} ${missingConfigNote}`);
      } else if (!runConfig) {
        showAlert("info", missingConfigNote);
      }
      // Set last so the status poller gets the matching expectedCount.
      setBatchRunId(taskId);
    },
    [applyRunConfig, setSearchParams, showAlert],
  );

  /**
   * Request cancellation of the selected batch.
   *
   * Deliberately leaves `selectedBatchStatus` as "pending" so progress polling keeps
   * running: for "now", the server stops the ECS tasks and marks rows cancelled straight
   * away, but the batch only reaches "cancelled" once the worker unwinds (up to one poll
   * interval); for "drain", companies with a running task carry on for hours. Either way
   * the existing report-phase poll is what reports the final state.
   *
   * @param {"drain" | "now"} mode
   */
  const handleCancelRun = async (mode) => {
    const taskId = batchRunId;
    setCancelDialogOpen(false);
    if (!taskId) {
      return;
    }

    let response;
    try {
      response = await cancelPipelineRun(taskId, mode);
    } catch {
      showAlert("error", "Could not cancel pipeline run.");
      return;
    }
    if (!(response instanceof Response)) {
      return; // handleHttpResponse already dealt with it (e.g. 403)
    }

    const body = await response.json().catch(() => ({}));
    if (response.status === 409) {
      // Finished between opening the dialog and confirming — show the real state.
      showAlert("info", "This run already finished — nothing to cancel.");
      selectRun(taskId, { force: true });
      loadPreviousRuns();
      return;
    }
    if (!response.ok) {
      showAlert("error", body.error || "Could not cancel pipeline run.");
      return;
    }

    if (mode === "drain") {
      setSelectedDraining(true);
      showAlert(
        "info",
        "Finishing running tasks — companies already classifying will get their sheet; " +
          "nothing new will start."
      );
      return;
    }

    const stopped = body.stopped_task_count ?? 0;
    showAlert(
      "warning",
      `Cancelling — stopped ${stopped} ECS task${stopped === 1 ? "" : "s"}. ` +
        "Companies will show as cancelled shortly."
    );
  };

  /** Cancel the company picked in the dialog; the rest of the batch carries on. */
  const handleCancelCompany = async () => {
    const companyName = companyToCancel;
    setCompanyToCancel(null);
    if (!batchRunId || !companyName) {
      return;
    }

    let response;
    try {
      response = await cancelPipelineCompany(batchRunId, companyName);
    } catch {
      showAlert("error", `Could not cancel ${companyName}.`);
      return;
    }
    if (!(response instanceof Response)) {
      return; // handleHttpResponse already dealt with it (e.g. 403)
    }

    const body = await response.json().catch(() => ({}));
    if (response.status === 409) {
      showAlert("info", `${companyName} already finished — nothing to cancel.`);
    } else if (!response.ok) {
      showAlert("error", body.error || `Could not cancel ${companyName}.`);
    } else {
      showAlert("warning", `Cancelled ${companyName} — the other companies carry on.`);
    }
    refreshPipelineRuns();
  };

  /** Load the previous-runs table from GET /get-pipeline-runs. */
  const loadPreviousRuns = useCallback(async () => {
    const response = await getPipelineRuns();
    if (!(response instanceof Response) || !response.ok) {
      showAlert("error", "Could not load previous pipeline runs.");
      return;
    }
    const data = await response.json();
    setPreviousRuns(mapPipelineHistoryRuns(data.pipeline_runs ?? []));
  }, [showAlert]);

  /** Load company names and previous runs on mount. */
  useEffect(() => {
    const loadCompanyNames = async () => {
      const response = await getMasterCompanyNames();
      if (response.status === 200) {
        const data = await response.json();
        const names = (data.company_names ?? []).slice().sort();
        setCompanyNames(names);
        return;
      }
      showAlert("error", "Could not load company names.");
    };
    loadCompanyNames();
    loadPreviousRuns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Restore the open run after refresh or a shared `?run=` link.
   * One attempt per id — a run whose celery log is missing (e.g. manually stopped
   * before the worker wrote it) must not be retried on every re-render.
   */
  useEffect(() => {
    if (runIdFromUrl && restoredRunIdRef.current !== runIdFromUrl) {
      restoredRunIdRef.current = runIdFromUrl;
      selectRun(runIdFromUrl, { syncUrl: false });
    }
  }, [runIdFromUrl, selectRun]);

  /** Keep client picks a subset of the selected companies. */
  const handleSelectedCompaniesChange = (companies) => {
    setSelectedCompanies(companies);
    setClientCompanies((prev) => prev.filter((name) => companies.includes(name)));
  };

  /** Enqueue the batch for the given companies. Assumes validation already passed. */
  const startBatch = async (companyNames) => {
    setIsSubmitting(true);
    setPipelineReport(null);
    showAlert("info", "Starting classification pipeline...");

    try {
      const companies = companyNames.map((name) => ({
        company_name: name,
        platforms: selectedPlatforms,
        start_date: dayjs(startDate).format("YYYY-MM-DD"),
        end_date: dayjs(endDate).format("YYYY-MM-DD"),
        include_previously_classified: includePreviouslyClassified,
        competitive_analysis: !clientCompanies.includes(name),
      }));

      const response = await initClassificationPipeline(companies, skipGeocoder);
      if (!(response instanceof Response)) {
        setIsSubmitting(false);
        return;
      }

      const body = await response.json();
      if (!response.ok) {
        showAlert("error", body.error || "Could not start classification pipeline.");
        setIsSubmitting(false);
        return;
      }

      const taskId = body.task_id;
      if (!taskId) {
        showAlert("error", "Could not get task id for classification pipeline.");
        setIsSubmitting(false);
        return;
      }

      // Mark selected before writing the URL so the restore effect is a no-op.
      selectedRunIdRef.current = taskId;
      setBatchRunId(taskId);
      setExpectedCount(companies.length);
      setSelectedBatchStatus("pending");
      setSelectedSkipGeocoder(skipGeocoder);
      setSelectedDraining(false);
      setSearchParams({ run: taskId }, { replace: true });
      showAlert("info", "Pipeline running — status updates every 30s...");
      loadPreviousRuns();
    } catch {
      showAlert("error", POLL_ALERT_COPY.fallback.message);
      setIsSubmitting(false);
    }
  };

  /**
   * Validate, run the pre-run data checks, then start the batch.
   *
   * A flagged company would be refused by the pipeline's own preflight_node, so there's
   * nothing to gain by starting it — the problems render at the top of the page and the
   * passing companies can go ahead without them. If the checks themselves can't be run,
   * the batch still starts: the pipeline repeats them per company, so the only thing lost
   * is the early warning.
   */
  const handleSubmit = async () => {
    if (selectedCompanies.length === 0) {
      showAlert("error", "Please select at least one company.");
      return;
    }
    if (selectedPlatforms.length === 0) {
      showAlert("error", "Please select at least one platform.");
      return;
    }
    if (startDate >= endDate) {
      showAlert("error", "Start date must be before end date.");
      return;
    }

    setIsSubmitting(true);
    showAlert("info", "Checking data...");
    const report = await runPreflightChecksForBatch(
      selectedCompanies,
      selectedPlatforms,
      dayjs(startDate).format("YYYY-MM-DD"),
      dayjs(endDate).format("YYYY-MM-DD"),
    );

    if (report === null) {
      showAlert("warning", "Could not run the data checks — starting anyway; the pipeline checks each company itself.");
      startBatch(selectedCompanies);
      return;
    }

    // The batch geocodes before classifying, so missing geocode only blocks a run that skips it.
    const results = skipGeocoder
      ? report
      : report.map((result) => {
          const issues = (result.issues ?? []).filter((issue) => issue.check !== "geocode");
          return { ...result, issues, passed: issues.length === 0 };
        });

    const failures = results.filter((result) => !result.passed);
    if (failures.length > 0) {
      setPreflightFailures(failures);
      setPreflightPassing(results.filter((result) => result.passed).map((result) => result.company_name));
      setIsSubmitting(false);
      // The details render at the top of the page, which may be scrolled well out of view.
      window.scrollTo({ top: 0, behavior: "smooth" });
      showAlert("warning", `${failures.length} of ${report.length} companies have data problems — details at the top of the page.`);
      return;
    }

    startBatch(selectedCompanies);
  };

  /** Start the batch with only the companies that passed, dropping the flagged ones. */
  const handleRunPassingOnly = () => {
    setPreflightFailures([]);
    startBatch(preflightPassing);
  };

  /**
   * Close the open run and clear the form, so another batch can be started.
   *
   * Only the page is reset — the closed run keeps going on the server and can be reopened
   * from Previous runs. Clearing selectedRunIdRef makes any in-flight fetch or report poll
   * for it ignore its result.
   */
  const handleNewRun = () => {
    selectedRunIdRef.current = null;
    setSearchParams({}, { replace: true });
    setBatchRunId(null);
    setExpectedCount(0);
    setSelectedBatchStatus(null);
    setSelectedSkipGeocoder(false);
    setSelectedDraining(false);
    setPipelineReport(null);
    setIsSubmitting(false);
    setPreflightFailures([]);
    setPreflightPassing([]);

    setSelectedCompanies([]);
    setClientCompanies([]);
    setSelectedPlatforms([]);
    setIncludePreviouslyClassified(true);
    setSkipGeocoder(false);
    setStartDate(dayjs().subtract(1, "day"));
    setEndDate(dayjs());
    closeAlert();
  };

  return (
    <Layout>
      <ValidationAlert
        inputOpen={alertState.alertOpen}
        setInputClosed={closeAlert}
        severity={alertState.alertType}
        alertMessage={alertState.alertMsg}
      />

      <CancelRunDialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        onConfirm={handleCancelRun}
        batchRunId={batchRunId}
        companyCount={pipelineRuns.length || expectedCount}
        draining={selectedDraining}
      />

      <CancelCompanyDialog
        companyName={companyToCancel}
        onClose={() => setCompanyToCancel(null)}
        onConfirm={handleCancelCompany}
      />

      <TopBarGridContainer>
        <Grid>
          <Title text={"CLASSIFICATION PIPELINE"} sx={{ my: { xs: 2, sm: 5 } }} />
        </Grid>
        <Grid size={{ xs: 12, sm: "grow" }}>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: { xs: "start", sm: "end" },
              mt: { xs: 0, sm: 5 },
              gap: 1,
            }}
          >
            <Button variant="outlined" color="primary" onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? "Running..." : "Run Pipeline"}
            </Button>
            {batchRunId && (
              <Button variant="outlined" color="primary" onClick={handleNewRun}>
                New Run
              </Button>
            )}
            <Button variant="outlined" color="error" onClick={() => navigate("/tasks")}>
              Cancel
            </Button>
          </Box>
        </Grid>
      </TopBarGridContainer>
      <Divider variant="middle" />

      <Grid container direction="column" wrap="nowrap" spacing={4} p={{ xs: 2, md: 3 }} mx={{ xs: 0, md: "10%" }}>
        {preflightFailures.length > 0 && (
          <Paper
            elevation={2}
            sx={{ p: 3, borderRadius: 2, borderLeft: 4, borderColor: "warning.main" }}
          >
            <Subheading text="Data problems found" sx={{ mb: 1 }} />
            <Box sx={{ fontSize: 14, mb: 2, color: "text.secondary" }}>
              Nothing started — {preflightFailures.length} of{" "}
              {preflightFailures.length + preflightPassing.length} companies need fixing.
            </Box>

            {preflightFailures.map((result) => (
              <Box key={result.company_name} sx={{ mb: 1.5 }}>
                <Box sx={{ fontSize: 14, fontWeight: "bold" }}>{result.company_name}</Box>
                {(result.issues ?? []).map((issue) => (
                  <Box
                    key={`${issue.check}-${issue.platform}`}
                    sx={{ display: "flex", gap: 2, fontSize: 13, pl: 1.5 }}
                  >
                    <Box sx={{ minWidth: 170, color: "warning.main" }}>
                      {CHECK_LABELS[issue.check] ?? issue.check}
                    </Box>
                    <Box sx={{ color: "text.secondary" }}>
                      {issue.platform} · {issue.row_count}{" "}
                      {issue.row_count === 1 ? "row" : "rows"}
                    </Box>
                  </Box>
                ))}
              </Box>
            ))}

            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              {preflightPassing.length > 0 && (
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleRunPassingOnly}
                  disabled={isSubmitting}
                >
                  Run {preflightPassing.length} passing{" "}
                  {preflightPassing.length === 1 ? "company" : "companies"}
                </Button>
              )}
              <Button variant="outlined" color="error" onClick={() => setPreflightFailures([])}>
                Dismiss
              </Button>
              {preflightPassing.length > 0 && (
                <Box sx={{ color: "text.secondary", fontSize: 13, ml: 1 }}>
                  Ready: {preflightPassing.join(", ")}
                </Box>
              )}
            </Box>
          </Paper>
        )}

        <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
          <Subheading text="What this runs" sx={{ mb: 1 }} />
          <Box
            component="ol"
            sx={{
              color: "text.secondary",
              fontSize: 14,
              m: 0,
              pl: 2.5,
              "& li": { mb: 0.75 },
            }}
          >
            <li>
              Before starting: check every company&apos;s data — flagged companies can be
              dropped from the batch
            </li>
            <li>
              {skipGeocoder
                ? "Geocode once for the whole batch — skipped"
                : "Geocode once for the whole batch"}
            </li>
            <li>
              Per company: check its data again just before launching its task, in case
              anything changed, then run auto4client classification (polls until done, one
              automatic retry on error)
            </li>
            <li>Generate a classified Google Sheet per company</li>
            <li>Write an LLM summary report for the batch</li>
          </Box>
          <Box sx={{ color: "text.secondary", fontSize: 14, mt: 1.5 }}>
            Batches can take several hours. Status updates in the table below
            while it runs.
          </Box>
        </Paper>

        <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
          <Autocomplete
            multiple
            options={companyNames}
            value={selectedCompanies}
            onChange={(event, value) => handleSelectedCompaniesChange(value)}
            renderInput={(params) => <TextField {...params} label="Select Companies" />}
            fullWidth
          />
          <Autocomplete
            multiple
            options={selectedCompanies}
            value={clientCompanies}
            onChange={(event, value) => setClientCompanies(value)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Client Companies"
                helperText="Companies listed here run full (client) mode; the rest run competitor mode."
              />
            )}
            fullWidth
            sx={{ mt: 3 }}
          />
        </Paper>

        <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
          <Subheading text="Platforms" sx={{ mb: 2 }} />
          <Box display="flex" flexDirection="column">
            {PLATFORM_NAMES.map((platform) => (
              <FormControlLabel
                key={platform}
                control={
                  <Checkbox
                    checked={selectedPlatforms.includes(platform)}
                    onChange={() => {
                      setSelectedPlatforms((prev) =>
                        prev.includes(platform)
                          ? prev.filter((p) => p !== platform)
                          : [...prev, platform]
                      );
                    }}
                  />
                }
                label={platform}
              />
            ))}
          </Box>
          <Divider sx={{ my: 2 }} />
          <FormControlLabel
            control={
              <Checkbox
                checked={includePreviouslyClassified}
                onChange={(e) => setIncludePreviouslyClassified(e.target.checked)}
              />
            }
            label="Include Previously Classified"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={skipGeocoder}
                onChange={(e) => setSkipGeocoder(e.target.checked)}
              />
            }
            label="Skip Geocoder"
          />
          <Box sx={{ color: "text.secondary", fontSize: 13, ml: 4 }}>
            Go straight to classification. Only safe if these platforms and dates have
            already been geocoded — otherwise classification runs on ungeocoded data.
          </Box>
        </Paper>

        <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
          <Grid container spacing={3} justifyContent="center">
            <Grid size={{ xs: 12, sm: 6 }}>
              <DatePicker currentVal={startDate} handleVal={setStartDate} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <DatePicker currentVal={endDate} handleVal={setEndDate} />
            </Grid>
          </Grid>
        </Paper>

        <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
          <Subheading text="Previous runs" sx={{ mb: 2 }} />
          <DataTable
            columns={PIPELINE_HISTORY_COLUMNS}
            dataRows={previousRuns}
            height={"40vh"}
            onRowClick={(params) => selectRun(params.id)}
            rowSelectionModel={batchRunId ? [batchRunId] : []}
            onRowSelectionModelChange={(ids) => {
              if (ids[0]) {
                selectRun(ids[0]);
              }
            }}
          />
        </Paper>

        {batchRunId && (
          <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <Box>
                <Subheading text="Run progress" sx={{ mb: 1 }} />
                <Box sx={{ color: "text.secondary", fontSize: 14, mb: 2 }}>
                  {selectedBatchStatus ?? "loading"} — {batchRunId}
                  {selectedSkipGeocoder && " — geocoder skipped"}
                  {selectedBatchStatus === "pending" && selectedDraining && " — finishing running tasks"}
                </Box>
              </Box>
              {selectedBatchStatus === "pending" && (
                <Button variant="outlined" color="error" onClick={() => setCancelDialogOpen(true)}>
                  {selectedDraining ? "Stop Now" : "Cancel Run"}
                </Button>
              )}
            </Box>
            {geocoderLogs.length > 0 && (
              <Box sx={{ color: "text.secondary", fontSize: 14, mb: 2 }}>
                Geocoder logs
                {geocoderLogs.map((log) => (
                  <Link
                    key={log.log_stream_name}
                    to={`/logs/${encodeURIComponent(log.log_stream_name)}`}
                    style={{ marginLeft: 12 }}
                  >
                    {log.platform || "Geocoder"}
                  </Link>
                ))}
              </Box>
            )}
            <DataTable
              columns={pipelineRunColumns}
              dataRows={pipelineRuns}
              height={"50vh"}
            />
          </Paper>
        )}

        {pipelineReport && (
          <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
            <Subheading text="Pipeline Report" sx={{ mb: 2 }} />
            <Box
              component="pre"
              sx={{
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                fontSize: 14,
                color: "text.secondary",
                m: 0,
              }}
            >
              {pipelineReport}
            </Box>
          </Paper>
        )}
      </Grid>
    </Layout>
  );
}

export default ClassificationPipeline;
